import { GitflicAuth } from '@backstage/core-app-api';
import {
  alertApiRef,
  ApiBlueprint,
  configApiRef,
  createFrontendModule,
  discoveryApiRef,
  gitflicAuthApiRef,
  oauthRequestApiRef,
  toastApiRef,
} from '@backstage/frontend-plugin-api';
import { ScmAuth, scmAuthApiRef } from '@backstage/integration-react';
import { GITFLIC_READ_SCOPES, GITFLIC_WRITE_SCOPES } from './gitflicScopes';

const toastApiExtension = ApiBlueprint.make({
  name: 'toast',
  params: defineParams =>
    defineParams({
      api: toastApiRef,
      deps: {
        alertApi: alertApiRef,
      },
      factory: ({ alertApi }) => ({
        post(toast) {
          let severity: 'error' | 'warning' | 'info' | 'success' = 'success';

          if (toast.status === 'danger') {
            severity = 'error';
          } else if (toast.status === 'warning') {
            severity = 'warning';
          } else if (toast.status === 'info') {
            severity = 'info';
          }

          const title = typeof toast.title === 'string' ? toast.title : '';
          const description =
            typeof toast.description === 'string' ? toast.description : '';
          const message = description ? `${title}: ${description}` : title;

          alertApi.post({
            message,
            severity,
            display: toast.timeout ? 'transient' : 'permanent',
          });

          return { close: () => {} };
        },
      }),
    }),
});

const scmAuthApiExtension = ApiBlueprint.make({
  name: 'scm-auth',
  params: defineParams =>
    defineParams({
      api: scmAuthApiRef,
      deps: {
        configApi: configApiRef,
        gitflic: gitflicAuthApiRef,
      },
      factory: ({ configApi, gitflic }) => {
        const gitflicHosts =
          configApi
            .getOptionalConfigArray('integrations.gitflic')
            ?.map(integration => integration.getString('host')) ?? [];

        const gitflicProviders =
          gitflicHosts.length > 0
            ? gitflicHosts.map(host => ScmAuth.forGitflic(gitflic, { host }))
            : [ScmAuth.forGitflic(gitflic)];

        return ScmAuth.merge(...gitflicProviders);
      },
    }),
});

const gitflicAuthApiExtension = ApiBlueprint.make({
  name: 'gitflic-auth',
  params: defineParams =>
    defineParams({
      api: gitflicAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        GitflicAuth.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          environment: configApi.getOptionalString('auth.environment'),
          defaultScopes: [...GITFLIC_READ_SCOPES, ...GITFLIC_WRITE_SCOPES],
        }),
    }),
});

export const apisModule = createFrontendModule({
  pluginId: 'app',
  extensions: [toastApiExtension, scmAuthApiExtension, gitflicAuthApiExtension],
});
